import {
    HeatmapSecuritiesResponse,
    HeatmapSecurityGroup,
    ScanScreenerResponse_ScreenerSecurityData,
} from '../../proto/generated/screener'

// ─── URL slug helpers ─────────────────────────────────────────────────────────

export function toHeatmapSlug(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/\s*&\s*/g, '-and-')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '')
}

export function indexSlugToValue(slug: string): string {
    return slug.trim().toUpperCase().replace(/-/g, ' ')
}

export function sectorSlugToValue(slug: string): string {
    return slug
        .trim()
        .split('-')
        .filter(Boolean)
        .map((w) => (w === 'and' ? '&' : w.charAt(0).toUpperCase() + w.slice(1)))
        .join(' ')
}

export function sectorSlugToValueFromConfig(slug: string, supportedSectors: string[]): string {
    const normalised = slug.trim().toLowerCase()
    const match =
        supportedSectors.find((s) => toHeatmapSlug(s) === normalised) ||
        supportedSectors.find((s) => toHeatmapSlug(s) === normalised.replace(/-&-/g, '-and-'))
    return match ?? sectorSlugToValue(slug)
}

export function indexValueToLabel(value: string): string {
    if (!value) return value
    const spaced = value.replace(/([A-Za-z])(\d)/g, '$1 $2')
    return spaced
        .split(' ')
        .map((word) => {
            if (!word) return word
            if (/^[A-Z]{2,4}$/.test(word)) return word
            if (/^\d+$/.test(word)) return word
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        })
        .join(' ')
}


/** Normalize index names so NIFTY_50 / NIFTY50 / "NIFTY 50" compare equal. */
export function normalizeHeatmapIndexKey(value: string): string {
    return value.replace(/[_\s-]/g, '').toUpperCase()
}

/**
 * Map chart security codes/symbols onto heatmap config `supportedIndices` values.
 * Returns the first supported index matching any candidate, or undefined.
 */
export function resolveHeatmapIndex(
    candidates: Array<string | null | undefined>,
    supportedIndices: string[],
): string | undefined {
    if (!supportedIndices.length) return undefined
    const byNorm = new Map(supportedIndices.map((index) => [normalizeHeatmapIndexKey(index), index]))

    for (const candidate of candidates) {
        if (!candidate?.trim()) continue
        const exact = supportedIndices.find((index) => index === candidate)
        if (exact) return exact
        const matched = byNorm.get(normalizeHeatmapIndexKey(candidate))
        if (matched) return matched
    }

    // Unique substring match (e.g. SENSEX ↔ BSE SENSEX). Skip if ambiguous (NIFTY ↔ NIFTY50/NIFTYBANK).
    for (const candidate of candidates) {
        if (!candidate?.trim()) continue
        const norm = normalizeHeatmapIndexKey(candidate)
        if (norm.length < 3) continue
        const partials = supportedIndices.filter((index) => {
            const key = normalizeHeatmapIndexKey(index)
            return key.includes(norm) || norm.includes(key)
        })
        if (partials.length === 1) return partials[0]
    }

    return undefined
}

/** Candidates used to map a chart security onto heatmap supportedIndices. */
export function heatmapIndexCandidatesFromSecurity(security: {
    code?: string | null
    symbol?: string | null
    displayName?: string | null
}): string[] {
    return [
        security.code,
        security.symbol,
        security.displayName,
        security.code?.replace(/_/g, ''),
        security.symbol?.replace(/_/g, ''),
    ].filter((v): v is string => Boolean(v?.trim()))
}

// ─── TradingView Stock Heatmap colour scale ───────────────────────────────────
// Dynamic ±range legend (TV autoscales — e.g. ±1.5%).
// Discrete swatches (tiles snap to legend colours). Multi-select legend filters.

/** Fallback when dataset is empty / all zeros. */
export const HEATMAP_DEFAULT_RANGE = 1.5

/**
 * TradingView Stock Heatmap swatches (from TV Nifty heatmap UI).
 * Ordered for ticks at [-range, -2r/3, -r/3, 0, r/3, 2r/3, range].
 */
/**
 * TradingView Stock Heatmap swatches (Finance sector comparison).
 * Greens use true green hue (not cyan/teal) so HDFC/ICICI read as green like TV.
 */
const LEGEND_SWATCH_HEX = [
    '#B71C2A', // deep red (extreme −)
    '#F23645', // TV bright red
    '#EF9A9A', // soft pink-red (slight −)
    '#B2B5BE', // TV neutral grey
    '#66BB6A', // clear light green (slight +) — was cyan #4ECDC4
    '#43A047', // solid medium green (moderate +) — was teal #00B897
    '#1B5E20', // deep forest (extreme +)
] as const

export type LegendBucket = {
    /** Index into legend ticks / segments */
    index: number
    /** Display label for the tick (e.g. "1.5%") */
    tick: number
    /**
     * Half-open range [min, max) — last bucket is [min, +Infinity].
     * Midpoint edges match TradingView outer segments.
     */
    min: number
    max: number
    /** Tooltip text e.g. "1% and above" */
    tooltip: string
    color: string
}

function formatTick(v: number): string {
    if (!Number.isFinite(v)) return ''
    const rounded = Math.round(v * 10) / 10
    const abs = Math.abs(rounded)
    const body = Number.isInteger(abs) ? String(abs) : abs.toFixed(1)
    if (rounded > 0) return `+${body}%`
    if (rounded < 0) return `-${body}%`
    return '0%'
}

/** Round a raw max-|change| into a TV-style nice half-range. */
export function niceHeatmapRange(raw: number): number {
    const floor = 0.6
    if (!Number.isFinite(raw) || raw <= 0) return HEATMAP_DEFAULT_RANGE
    // Prefer values TV commonly shows for daily → yearly metrics.
    const candidates = [
        0.6, 0.9, 1.2, 1.5, 1.8, 2.4, 3.0, 3.6, 4.5, 5.5, 6.0, 7.5, 9.0, 12, 15, 18, 24, 30, 36, 38, 42, 45, 50, 55, 60, 75,
        90, 100, 120, 150, 200,
    ]
    for (const c of candidates) {
        if (raw <= c) return Math.max(c, floor)
    }
    return Math.ceil(raw / 10) * 10
}

/**
 * Always exactly 7 legend ticks (±range equal thirds) — TradingView style.
 * Never emit denser ladders; large yearly ranges (e.g. ±85) previously overflowed the UI.
 */
export function buildLegendTicks(range = HEATMAP_DEFAULT_RANGE): number[] {
    const absMax = Math.max(range, 0.1)
    const step = absMax / 3
    // Integers for large % legends (1Y); one decimal for small daily/weekly ranges.
    const roundTick = (t: number) => {
        if (Math.abs(t) < 1e-9) return 0
        if (absMax >= 20) return Math.round(t)
        return Math.round(t * 10) / 10
    }
    return [-absMax, -2 * step, -step, 0, step, 2 * step, absMax].map(roundTick)
}

/**
 * Build 7 clickable legend buckets for ±range.
 * Segment edges sit at midpoints between adjacent ticks (TradingView style).
 */
export function buildLegendBuckets(range = HEATMAP_DEFAULT_RANGE): LegendBucket[] {
    const ticks = buildLegendTicks(range)

    return ticks.map((tick, i) => {
        const prev = ticks[i - 1]
        const next = ticks[i + 1]
        const min = prev === undefined ? -Infinity : (prev + tick) / 2
        const max = next === undefined ? Infinity : (tick + next) / 2

        let tooltip: string
        if (prev === undefined) {
            tooltip = `${formatTick(max)} and below`
        } else if (next === undefined) {
            tooltip = `${formatTick(min)} and above`
        } else {
            tooltip = `${formatTick(min)} to ${formatTick(max)}`
        }

        return {
            index: i,
            tick,
            min,
            max,
            tooltip,
            color: LEGEND_SWATCH_HEX[i] ?? LEGEND_SWATCH_HEX[3],
        }
    })
}

/** All legend indices (0..6) — default multi-select state. */
export function allLegendIndices(range = HEATMAP_DEFAULT_RANGE): number[] {
    return buildLegendBuckets(range).map((b) => b.index)
}

/** Which legend bucket a % change maps into (same banding used for colour + filter). */
export function bucketForValue(value: number | null, range = HEATMAP_DEFAULT_RANGE): LegendBucket | null {
    if (value === null || !Number.isFinite(value)) return null
    const buckets = buildLegendBuckets(range)
    for (let i = 0; i < buckets.length; i++) {
        const b = buckets[i]
        const isLast = i === buckets.length - 1
        if (isLast ? value >= b.min : value >= b.min && value < b.max) return b
    }
    return buckets[buckets.length - 1]
}

/** Discrete TV swatch — tile colour matches its legend segment exactly. */
export function colorForValue(value: number | null, range = HEATMAP_DEFAULT_RANGE): string {
    const b = bucketForValue(value, range)
    return b?.color ?? LEGEND_SWATCH_HEX[3]
}

/** White labels on coloured tiles (TV style). */
export function textColorForValue(_value: number | null, _range = HEATMAP_DEFAULT_RANGE): string {
    return '#ffffff'
}

/**
 * True when `value` falls in any of the selected legend bands.
 * Empty set → nothing matches. `null` selectedIndices → everything matches.
 */
export function matchesSelectedLegendIndices(
    value: number | null,
    selectedIndices: ReadonlySet<number> | null,
    range = HEATMAP_DEFAULT_RANGE
): boolean {
    if (selectedIndices === null) return true
    if (selectedIndices.size === 0) return false
    if (value === null || !Number.isFinite(value)) return false
    const resolved = bucketForValue(value, range)
    return resolved !== null && selectedIndices.has(resolved.index)
}

/** @deprecated use matchesSelectedLegendIndices — kept for single-bucket checks */
export function matchesLegendBucket(
    value: number | null,
    bucket: LegendBucket | null,
    range = HEATMAP_DEFAULT_RANGE
): boolean {
    if (!bucket) return true
    return matchesSelectedLegendIndices(value, new Set([bucket.index]), range)
}

// ─── Squarify treemap layout (Bruls / Huizing / van Wijk) ─────────────────────

export type Rect = { x: number; y: number; w: number; h: number }

/** Worst aspect ratio of rectangles in the current row (lower is better). */
function worstAspect(rowAreas: number[], shortSide: number): number {
    if (!rowAreas.length || shortSide <= 0) return Infinity
    let sum = 0
    let maxA = -Infinity
    let minA = Infinity
    for (const a of rowAreas) {
        sum += a
        if (a > maxA) maxA = a
        if (a < minA) minA = a
    }
    if (sum <= 0 || minA <= 0) return Infinity
    const s2 = sum * sum
    const w2 = shortSide * shortSide
    return Math.max((w2 * maxA) / s2, s2 / (w2 * minA))
}

function layoutRow(
    areas: number[],
    start: number,
    end: number,
    x: number,
    y: number,
    w: number,
    h: number,
    horizontal: boolean,
    rects: Rect[]
): void {
    let rowSum = 0
    for (let i = start; i < end; i++) rowSum += areas[i]
    if (rowSum <= 0) return

    if (horizontal) {
        const thickness = rowSum / h
        let cy = y
        for (let i = start; i < end; i++) {
            const th = (areas[i] / rowSum) * h
            rects[i] = { x, y: cy, w: thickness, h: th }
            cy += th
        }
    } else {
        const thickness = rowSum / w
        let cx = x
        for (let i = start; i < end; i++) {
            const tw = (areas[i] / rowSum) * w
            rects[i] = { x: cx, y, w: tw, h: thickness }
            cx += tw
        }
    }
}

function squarifyRecursive(
    areas: number[],
    start: number,
    x: number,
    y: number,
    w: number,
    h: number,
    rects: Rect[]
): void {
    const n = areas.length
    if (start >= n || w <= 0 || h <= 0) return

    if (start === n - 1) {
        rects[start] = { x, y, w, h }
        return
    }

    const horizontal = w >= h
    const shortSide = horizontal ? h : w

    // Grow a row while adding the next item improves (or holds) worst aspect ratio.
    const row: number[] = []
    let rowEnd = start
    for (let i = start; i < n; i++) {
        const next = [...row, areas[i]]
        if (row.length > 0 && worstAspect(next, shortSide) > worstAspect(row, shortSide)) break
        row.push(areas[i])
        rowEnd = i + 1
    }

    layoutRow(areas, start, rowEnd, x, y, w, h, horizontal, rects)

    let rowSum = 0
    for (let i = start; i < rowEnd; i++) rowSum += areas[i]
    const thickness = shortSide > 0 ? rowSum / shortSide : 0

    if (rowEnd < n) {
        if (horizontal) {
            squarifyRecursive(areas, rowEnd, x + thickness, y, w - thickness, h, rects)
        } else {
            squarifyRecursive(areas, rowEnd, x, y + thickness, w, h - thickness, rects)
        }
    }
}

/**
 * Squarified treemap layout. `items` should already be sorted by sizeValue desc.
 * Returns one Rect per item, same index order.
 */
export function squarify(items: { sizeValue: number }[], x: number, y: number, w: number, h: number): Rect[] {
    if (!items.length || w <= 0 || h <= 0) return []
    const total = items.reduce((s, c) => s + Math.max(0, c.sizeValue || 0), 0)
    if (total <= 0) return items.map(() => ({ x, y, w: 0, h: 0 }))

    const area = w * h
    const areas = items.map((c) => (Math.max(0, c.sizeValue || 0) / total) * area)
    const rects: Rect[] = new Array(items.length)
    squarifyRecursive(areas, 0, x, y, w, h, rects)
    // Guard any unset slots
    for (let i = 0; i < rects.length; i++) {
        if (!rects[i]) rects[i] = { x, y, w: 0, h: 0 }
    }
    return rects
}

// ─── Parsed / UI-friendly types ───────────────────────────────────────────────

export type ParsedSecurity = {
    symbol: string
    exchange: string
    name: string
    logoUrl: string | null
    price: number | null
    change1d: number | null
    marketCap: number
    sector: string
    urlIdentifier: string
    sizeValue: number
    layoutWeight: number // raw sizeValue — used for squarify
    colorValue: number | null
}

export type ParsedGroup = {
    groupName: string
    securities: ParsedSecurity[]
    totalWeight: number
}

export type ParsedHeatmapData = {
    groups: ParsedGroup[]
    securities: ParsedSecurity[]
    sizeByField: string
    colorByField: string
}

function cellAt(data: string[], fields: string[], code: string): string {
    const i = fields.indexOf(code)
    return i >= 0 ? (data[i] ?? '') : ''
}

function numAt(data: string[], fields: string[], code: string): number | null {
    const raw = cellAt(data, fields, code)
    if (!raw || raw === 'null' || raw === 'NaN') return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
}

function parseRow(
    row: ScanScreenerResponse_ScreenerSecurityData,
    fields: string[],
    sizeByField: string,
    colorByField: string
): ParsedSecurity | null {
    const data = row.data ?? []
    const marketCap = numAt(data, fields, 'marketCap') ?? 0
    if (marketCap <= 0) return null

    // Do not silently fall back to marketCap — that made broken size-by options look identical.
    const sizeValue = numAt(data, fields, sizeByField)
    if (sizeValue === null || sizeValue <= 0) return null

    return {
        symbol: row.code ?? '',
        exchange: String(row.exchange ?? ''),
        name: cellAt(data, fields, 'name') || row.code,
        logoUrl: cellAt(data, fields, 'logoUrl') || null,
        price: numAt(data, fields, 'price'),
        change1d: numAt(data, fields, 'change|1D'),
        marketCap,
        sector: cellAt(data, fields, 'sector') || 'Others',
        urlIdentifier: cellAt(data, fields, 'urlIdentifier') || row.code.toLowerCase(),
        sizeValue,
        layoutWeight: sizeValue,
        colorValue: numAt(data, fields, colorByField),
    }
}

export function parseHeatmapResponse(resp: HeatmapSecuritiesResponse): ParsedHeatmapData {
    const fields = resp.fields ?? []
    const sf = resp.sizeByField || 'marketCap'
    const cf = resp.colorByField || 'change|1D'

    if (resp.groups?.length) {
        const groups: ParsedGroup[] = resp.groups
            .map((g: HeatmapSecurityGroup) => {
                const securities = g.securities
                    .map((r) => parseRow(r, fields, sf, cf))
                    .filter((s): s is ParsedSecurity => s !== null)
                    .sort((a, b) => b.layoutWeight - a.layoutWeight)
                return {
                    groupName: g.groupName,
                    securities,
                    totalWeight: securities.reduce((sum, s) => sum + s.layoutWeight, 0),
                }
            })
            .filter((g) => g.securities.length > 0)
            .sort((a, b) => b.totalWeight - a.totalWeight)

        return { groups, securities: [], sizeByField: sf, colorByField: cf }
    }

    const securities = (resp.securities ?? [])
        .map((r) => parseRow(r, fields, sf, cf))
        .filter((s): s is ParsedSecurity => s !== null)
        .sort((a, b) => b.layoutWeight - a.layoutWeight)

    return { groups: [], securities, sizeByField: sf, colorByField: cf }
}

/** Filter by multi-selected legend bands (relayouts around survivors).
 *  `selectedIndices === null` → all bands (unfiltered).
 *  empty set → no securities (TV "No data match your criteria").
 */
export function filterHeatmapData(
    data: ParsedHeatmapData,
    selectedIndices: ReadonlySet<number> | null,
    range = HEATMAP_DEFAULT_RANGE
): ParsedHeatmapData {
    if (selectedIndices === null) return data
    if (selectedIndices.size === 0) {
        return { ...data, groups: [], securities: [] }
    }
    // All 7 bands selected → nothing to filter
    if (selectedIndices.size >= 7) return data

    if (data.groups.length > 0) {
        const groups = data.groups
            .map((g) => {
                const securities = g.securities.filter((s) =>
                    matchesSelectedLegendIndices(s.colorValue, selectedIndices, range)
                )
                return {
                    ...g,
                    securities,
                    totalWeight: securities.reduce((sum, s) => sum + s.layoutWeight, 0),
                }
            })
            .filter((g) => g.securities.length > 0)
        return { ...data, groups, securities: [] }
    }

    return {
        ...data,
        securities: data.securities.filter((s) => matchesSelectedLegendIndices(s.colorValue, selectedIndices, range)),
    }
}

export function computeColorRange(data: ParsedHeatmapData): number {
    const allValues: number[] = []
    const collect = (secs: ParsedSecurity[]) => {
        for (const s of secs) {
            if (s.colorValue !== null && Number.isFinite(s.colorValue)) {
                allValues.push(Math.abs(s.colorValue))
            }
        }
    }
    data.groups.forEach((g) => collect(g.securities))
    collect(data.securities)
    if (!allValues.length) return HEATMAP_DEFAULT_RANGE

    // Autoscale to the bulk of values (p90). Absolute max would blow out the legend
    // on yearly metrics (e.g. one ±85% name → dozens of useless ticks / all-grey tiles).
    // Outliers still map into the extreme red/green buckets.
    allValues.sort((a, b) => a - b)
    const p90Index = Math.min(allValues.length - 1, Math.floor(allValues.length * 0.9))
    const p90 = allValues[p90Index]
    return niceHeatmapRange(p90)
}

// Selectors
export const selectHeatmapStore = (state: any) => state.heatmapStore
